import { stringLiteral } from '@execonline-inc/decoders';
import { Maybe, structuredMaybeDecoder } from '@execonline-inc/structured-maybe';
import { Decoder, array, date, field, number, oneOf, string, succeed } from 'jsonous';
import { resourceDecoder } from '../../../../Resource/Decoders';
import { Resource } from '../../../../Resource/Types';

// Base types
export interface ProductLicensePayload {
  id: number;
  name: string;
  publicName: Maybe<string>;
  endOn: Date; // Comes as string from server
}

// A single product license with its links
export type ProductLicenseResource = Resource<ProductLicensePayload>;

// Collection of product licenses wrapped in a resource
export type ProductLicensesResource = Resource<ProductLicenseResource[]>;

// License insight types
export type LicenseInsightKind =
  | 'invite-more-participants-alert'
  | 'expiration-alert'
  | 'overallocation-alert';

export interface LicenseInsightPayload {
  kind: LicenseInsightKind;
  productLicenses: ProductLicensesResource;
  overallocatedBy: Maybe<number>;
}

export type LicenseInsightResource = Resource<LicenseInsightPayload>;

export type LicenseInsightsResource = Resource<LicenseInsightResource[]>;

export const productLicensePayloadDecoder: Decoder<ProductLicensePayload> = succeed({})
  .assign('id', field('id', number))
  .assign('name', field('name', string))
  .assign('publicName', field('public_name', structuredMaybeDecoder(string)))
  .assign('endOn', field('end_on', date));

export const productLicenseResourceDecoder: Decoder<ProductLicenseResource> = resourceDecoder(
  productLicensePayloadDecoder,
);

const productLicensesResourceDecoder: Decoder<ProductLicensesResource> = resourceDecoder(
  array(productLicenseResourceDecoder),
);

export const licenseInsightKindDecoder: Decoder<LicenseInsightKind> = oneOf([
  stringLiteral<LicenseInsightKind>('invite-more-participants-alert'),
  stringLiteral<LicenseInsightKind>('expiration-alert'),
  stringLiteral<LicenseInsightKind>('overallocation-alert'),
]);

export const licenseInsightPayloadDecoder: Decoder<LicenseInsightPayload> = succeed({})
  .assign('kind', field('kind', licenseInsightKindDecoder))
  .assign('productLicenses', field('product_licenses', productLicensesResourceDecoder))
  .assign('overallocatedBy', field('overallocated_by', structuredMaybeDecoder(number)));

export const licenseInsightResourceDecoder: Decoder<LicenseInsightResource> = resourceDecoder(
  licenseInsightPayloadDecoder,
);

export const licenseInsightsResourceDecoder: Decoder<LicenseInsightsResource> = resourceDecoder(
  array(licenseInsightResourceDecoder),
);
